import { useState } from "preact/compat"
import useSWR from "swr"
import { IntegrationWithJobInfo, getIntegrationsWithJobs } from "@/api/integration"
import { MailboxProvider, provider_display_name, selfConnectMailbox } from "@/api/mailbox_invite"
import { Button } from "@/components/ui/button"
import { mailboxes_needing_reconnect, reconnect_mode } from "./known_mailbox"

/** The org's connections, shared by everything on a page that needs to know which mailboxes exist. */
export function useConnectedIntegrations(): IntegrationWithJobInfo[] {
  // The cache key is shared with the settings pages, which store the whole `Result` under it, so
  // this must fetch the same shape — caching the bare list here made their reads (and this one,
  // after theirs) see the wrong type: `e.filter is not a function` on /settings/connections.
  const { data } = useSWR("integrations-with-jobs", () => getIntegrationsWithJobs())
  return data?.ok ? data.value.data : []
}

interface ReconnectMailboxesProps {
  /** Where to return after the provider sign-in. Defaults to the current page. */
  next?: string
  className?: string
}

/**
 * One button per mailbox whose sign-in the provider has rejected. The mailbox and its provider are
 * both already known, so there is nothing to type or choose — the click goes straight to the right
 * sign-in. Renders nothing when every mailbox is healthy.
 */
export function ReconnectMailboxes({ next, className }: ReconnectMailboxesProps) {
  const integrations = useConnectedIntegrations()
  const [busy_id, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const broken = mailboxes_needing_reconnect(integrations)

  async function handle_reconnect(integration: IntegrationWithJobInfo) {
    setError(null)
    setBusyId(integration.id)
    const res = await selfConnectMailbox({
      integration_name: integration.name as MailboxProvider,
      email: integration.username,
      mode: reconnect_mode(integration),
      next: next ?? window.location.pathname,
    })
    if (!res.ok) {
      setBusyId(null)
      setError(res.error.message)
      return
    }
    window.location.href = res.value.data
  }

  if (broken.length === 0) return null

  return (
    <div class={className}>
      <div class="border border-red-200 bg-red-50 rounded-lg p-4 space-y-3">
        <p class="text-sm text-red-800">
          {broken.length === 1 ? "This mailbox has" : "These mailboxes have"} stopped syncing because the sign-in
          expired. Reconnect to resume — nothing that arrived in the meantime is lost.
        </p>
        {broken.map((integration: IntegrationWithJobInfo) => {
          const provider = provider_display_name(integration.name as MailboxProvider)
          return (
            <div key={integration.id} class="flex flex-wrap items-center justify-between gap-3">
              <div class="min-w-0">
                <div class="text-sm font-medium text-gray-900 truncate">{integration.username}</div>
                <div class="text-xs text-gray-600">
                  {provider} mailbox — sign in as {integration.username} when {integration.name === "gmail" ? "Google" : "Microsoft"} asks.
                </div>
              </div>
              <Button type="button" disabled={busy_id !== null} onClick={() => handle_reconnect(integration)}>
                Reconnect with {integration.name === "gmail" ? "Google" : "Microsoft"}
              </Button>
            </div>
          )
        })}
        {error && <p class="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  )
}
